import { useState, useMemo } from "react"
import { Link } from "react-router-dom"
import {
    ArrowDown,
    Leaf,
    Bug,
    Sprout,
    Recycle,
    FolderOpen,
    Layers,
    FileText,
} from "lucide-react"

// ——— Research projects config: add new projects here ———
const CATEGORY_ICONS = { Bug, Leaf, Recycle, Sprout }

const RESEARCH_PROJECTS = [
    {
        id: "vermicompostage",
        name: "Vermicompostage",
        shortName: "Vermicompostage",
        tagline: "Lombricompost & agriculture durable",
        description: [
            "Le projet de vermicompostage de l'Université de Ngozi vise à valoriser les déchets organiques par la lombriculture pour produire un amendement de qualité et renforcer la fertilité des sols.",
            "Ce projet regroupe des travaux de recherche sur la lombriculture, la fertilité des sols, la valorisation des déchets organiques et la formation des acteurs.",
        ],
        categories: [
            { title: "Lombriculture", desc: "Élevage de vers et production de lombricompost à l'échelle pilote.", icon: "Bug" },
            { title: "Fertilité des sols", desc: "Amendement organique, structure du sol et cultures.", icon: "Leaf" },
            { title: "Valorisation des déchets", desc: "Déchets organiques, compostage et économie circulaire.", icon: "Recycle" },
            { title: "Formation & transfert", desc: "Capacitation des acteurs et diffusion des pratiques.", icon: "Sprout" },
        ],
        partnerLogos: [],
    },
    // Add more projects here as needed.
]




export default function Publications() {
    const [selectedProjectId, setSelectedProjectId] = useState(RESEARCH_PROJECTS[0]?.id ?? null)

    const selectedProject = useMemo(
        () => RESEARCH_PROJECTS.find((p) => p.id === selectedProjectId) ?? RESEARCH_PROJECTS[0],
        [selectedProjectId]
    )

    const stats = useMemo(
        () => [
            { label: "Projets", value: String(RESEARCH_PROJECTS.length), icon: Layers }
        ],
        []
    )

    return (
        <div className="bg-background min-h-screen">
            {/* Hero — generic for all projects */}
            <header className="relative min-h-[50vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/70" />
                    <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,hsl(var(--accent)),transparent)]" />
                </div>
                <div className="container relative z-10 py-16">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-3 mb-6">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">
                                Recherche
                            </span>
                            <span className="text-[10px] text-primary-foreground/70 uppercase tracking-widest font-bold">
                                Publications
                            </span>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-white leading-[1.05] mb-6">
                            <span className="block italic font-light text-accent/90">Projets</span>
                            <span className="block text-white mt-1">de recherche</span>
                        </h1>
                        <p className="text-lg text-primary-foreground/90 max-w-xl mb-10 leading-relaxed">
                            Découvrez les projets et travaux de recherche de l&apos;Université de Ngozi.
                        </p>
                        <a
                            href="#projects"
                            className="inline-flex items-center gap-2 text-primary-foreground/90 hover:text-white text-sm font-semibold transition-colors"
                        >
                            Choisir un projet
                            <ArrowDown className="w-4 h-4 animate-bounce" />
                        </a>
                    </div>
                </div>
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-px h-10 bg-white/30 rounded-full" />
            </header>

            {/* Stats */}
            <section className="border-b border-border bg-secondary/30">
                <div className="container py-6">
                    <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16">
                        {stats.map((s, i) => {
                            const Icon = s.icon
                            return (
                                <div key={i} className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-xl bg-primary/10">
                                        <Icon className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <span className="text-2xl font-black text-foreground tabular-nums">{s.value}</span>
                                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold ml-2">
                                            {s.label}
                                        </span>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </section>

            <section id="projects" className="py-12 md:py-16 bg-background scroll-mt-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-8">
                        <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
                        <span>/</span>
                        <Link to="/recherche/publications" className="hover:text-primary transition-colors">Recherche</Link>
                        <span>/</span>
                        <span className="text-foreground font-medium">{selectedProject?.name ?? "Publications"}</span>
                    </nav>

                    {/* Project selector — visible when more than one project */}
                    {RESEARCH_PROJECTS.length > 1 && (
                        <div className="mb-10">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Choisir un projet</p>
                            <div className="flex flex-wrap gap-3">
                                {RESEARCH_PROJECTS.map((proj) => {
                                    const isSelected = proj.id === selectedProjectId
                                    return (
                                        <button
                                            key={proj.id}
                                            type="button"
                                            onClick={() => setSelectedProjectId(proj.id)}
                                            className={`flex items-center gap-3 px-5 py-3 rounded-xl border-2 transition-all text-left ${
                                                isSelected
                                                    ? "border-primary bg-primary/10 text-primary shadow-sm"
                                                    : "border-border bg-card hover:border-primary/40 hover:bg-secondary/50"
                                            }`}
                                        >
                                            <FolderOpen className={`w-5 h-5 flex-shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                                            <div>
                                                <span className="block font-bold text-foreground text-sm">{proj.name}</span>
                                            </div>
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    )}

                    {/* Selected project content */}
                    {selectedProject && (
                        <>
                            {/* Intro + categories */}
                            <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                                <div className="lg:col-span-5">
                                    <span className="inline-block px-3 py-1 bg-accent/15 text-accent text-[10px] uppercase tracking-[0.2em] font-black rounded-full border border-accent/25 mb-6">
                                        {selectedProject.shortName}
                                    </span>
                                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground leading-tight mb-6">
                                        {selectedProject.tagline}
                                    </h2>
                                    <div className="space-y-4 text-muted-foreground leading-relaxed">
                                        {selectedProject.description.map((para, i) => (
                                            <p key={i} className="text-base">
                                                {para}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                                <div className="lg:col-span-7">
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        {selectedProject.categories.map((item, i) => {
                                            const Icon = CATEGORY_ICONS[item.icon] ?? FileText
                                            return (
                                                <div
                                                    key={i}
                                                    className="group p-5 rounded-2xl border border-border bg-card hover:border-primary/30 hover:shadow-lg transition-all duration-300"
                                                >
                                                    <div className="flex items-start gap-4">
                                                        <div className="flex-shrink-0 p-2.5 rounded-xl bg-primary/10 text-primary group-hover:bg-accent/20 group-hover:text-accent transition-colors">
                                                            <Icon className="w-6 h-6" />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-bold text-foreground text-sm mb-1">{item.title}</h3>
                                                            <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Partners */}
                        </>
                    )}
                </div>
            </section>
        </div>
    )
}
